import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import { SpacedAutocompleteInput } from "components/common/ui/SpacedTextInput";
import { Box, Grid, Tooltip } from "@mui/material";
import EditSection from "components/common/ui/EditSection";
import DeleteIcon from "@mui/icons-material/Delete";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import PillButton from "components/common/ui/PillButton";
import { DisplayMenuSection } from "models/menu/menuContentsReducer";
import MenuItemMainEditBox from "components/menu/ui/MenuItemMainEditBox";
import { MenuContentEditSectionProps } from "components/menu/ui/MenuContentsEditSection";
import { getMenuSectionNameOptions } from "models/menu/constants/sectionName";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CircleIconButton from "components/common/ui/CircleIconButton";
import {
  combineUUIDs,
  getMenuOptionByPath,
} from "utils/menu/menu/menuContentDisplay";
import { isDisplayMenuItem } from "models/menu/menu";
import useMenuOptionActions from "hooks/menu/useMenuOptionActions";

const MenuGroupNameTextField = styled(SpacedAutocompleteInput)`
  background: rgb(244, 244, 244);
  border-radius: 0.5rem;
` as typeof SpacedAutocompleteInput;

export interface MenuOptionEditBoxProps extends MenuContentEditSectionProps {
  displayMenuOptionUUIDPath: string;
}

const MenuSectionEditBox: FC<MenuOptionEditBoxProps> = ({
  foodItemOptions,
  displayMenuOptionUUIDPath,
  menuContents,
  dispatchMenuContents,
}) => {
  const [sectionName, setSectionName] = useState("");
  const { handleMove, handleRemove, handleAdd, handleUpdate } =
    useMenuOptionActions(
      menuContents,
      dispatchMenuContents,
      displayMenuOptionUUIDPath,
    );

  /**
   * Use effect to make sure the section name displaying are sync
   */
  useEffect(() => {
    const displayOptionGroupOption = getMenuOptionByPath(
      menuContents,
      displayMenuOptionUUIDPath,
    );
    if (
      !!displayOptionGroupOption &&
      !isDisplayMenuItem(displayOptionGroupOption)
    ) {
      setSectionName(displayOptionGroupOption.name);
    }
  }, [displayMenuOptionUUIDPath, menuContents]);

  /**
   * Update the menu section
   */
  const handleUpdateMenuSection = useCallback(() => {
    handleUpdate({ name: sectionName });
  }, [handleUpdate, sectionName]);

  /**
   * Handle change menu section name
   */
  const handleChangeMenuSectionName = useCallback(
    (_: SyntheticEvent, newValue: string | null) => {
      if (!newValue) {
        setSectionName("");
      } else {
        setSectionName(newValue);
      }
    },
    [],
  );

  return (
    <EditSection>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-start">
            <Box display="flex" sx={{ paddingRight: 1 }} alignSelf="center">
              <Box sx={{ paddingRight: 1 }}>
                <Tooltip title="Move this section and it's content up">
                  <CircleIconButton sizepx={46} onClick={handleMove("up")}>
                    <KeyboardArrowUpIcon />
                  </CircleIconButton>
                </Tooltip>
              </Box>
              <Box>
                <Tooltip title="Move this section and it's content down">
                  <CircleIconButton sizepx={46} onClick={handleMove("down")}>
                    <KeyboardArrowDownIcon />
                  </CircleIconButton>
                </Tooltip>
              </Box>
            </Box>
            <MenuGroupNameTextField
              label="Menu Section Name"
              value={sectionName ?? ""}
              onBlur={handleUpdateMenuSection}
              onChange={handleChangeMenuSectionName}
              options={getMenuSectionNameOptions()}
              error={!sectionName}
              shrink
            />
            <Box sx={{ paddingLeft: 1 }}>
              <Tooltip title="Remove this section">
                <SoftSquareIconButton
                  positive
                  danger
                  sizepx={56}
                  onClick={handleRemove}
                >
                  <DeleteIcon />
                </SoftSquareIconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>
        {Object.keys(
          (
            getMenuOptionByPath(
              menuContents,
              displayMenuOptionUUIDPath,
            ) as DisplayMenuSection
          ).options,
        ).map((itemMainUUID) => (
          <Grid key={itemMainUUID} item xs={12}>
            <MenuItemMainEditBox
              displayMenuOptionUUIDPath={combineUUIDs([
                displayMenuOptionUUIDPath,
                itemMainUUID,
              ])}
              menuContents={menuContents}
              dispatchMenuContents={dispatchMenuContents}
              foodItemOptions={foodItemOptions}
            />
          </Grid>
        ))}
        <PillButton text="Add item above" onClick={handleAdd("menu item")} />
      </Grid>
    </EditSection>
  );
};

export default MenuSectionEditBox;
